/**
 * Iframe utility functions for seamless parent-child communication
 * Optimized for performance with debouncing and message coalescing
 */

import { performanceMonitor } from './iframe-performance-monitor';

export interface IframeMessage {
  type: string;
  payload?: any;
}

/**
 * Message types that can be sent from parent to iframe:
 * - INIT_AUTH: Initial authentication data (sent after IFRAME_READY)
 * - AUTH_REFRESH: Updated authentication token (sent when token is refreshed)
 * - SET_THEME: Change the theme (light/dark)
 * - MODAL_REOPENED: Modal was closed and reopened with fresh params
 * - REFRESH_PARAMS: Force refresh of URL parameters
 * 
 * Message types sent from iframe to parent:
 * - IFRAME_READY: Iframe has loaded and is ready (waiting for INIT_AUTH)
 * - RESIZE_IFRAME: Request iframe height adjustment
 * - RESIZE_MODAL: Request modal size change
 * - WITHDRAWAL_COMPLETE: Withdrawal succeeded or failed
 * - CLOSE_MODAL: Request to close the modal
 * - UPDATE_BANKING_DETAILS: User wants to update banking details
 * - OFFLINE_WITHDRAWAL_STARTED: Offline withdrawal initiated
 * - OFFLINE_MODAL_STEP: Offline modal step changed
 */

/**
 * Check if the application is running inside an iframe
 */
export const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

/**
 * Send message to parent window
 * Includes performance monitoring in development mode
 */
export const sendMessageToParent = (message: IframeMessage): void => {
  if (isInIframe() && window.parent) {
    // Track message for performance monitoring
    performanceMonitor.trackMessage(message.type);

    window.parent.postMessage(message, '*');
  }
};

// Performance optimization: track last height and debounce timer
let lastHeight = 0;
let debounceTimer: number | null = null;
let rafId: number | null = null;

/**
 * Notify parent of height changes for dynamic iframe resizing
 * Optimized with debouncing and change threshold to prevent message spam
 */
export const notifyHeightChange = (immediate: boolean = false): void => {
  if (!isInIframe()) return;

  const height = document.documentElement.scrollHeight;

  // Ignore tiny changes (< 5px) to prevent spam during micro-adjustments
  if (Math.abs(height - lastHeight) < 5 && !immediate) {
    return;
  }

  lastHeight = height;

  // If immediate, send right away (for explicit state changes)
  if (immediate) {
    sendMessageToParent({
      type: 'RESIZE_IFRAME',
      payload: { height }
    });
    return;
  }

  // Otherwise, debounce using requestAnimationFrame for smooth updates
  if (rafId) {
    cancelAnimationFrame(rafId);
  }

  rafId = requestAnimationFrame(() => {
    sendMessageToParent({
      type: 'RESIZE_IFRAME',
      payload: { height }
    });
    rafId = null;
  });
};

/**
 * Setup resize observer for automatic height updates
 * Optimized with debouncing to prevent message spam during animations
 */
export const setupAutoResize = (): (() => void) => {
  if (!isInIframe()) {
    return () => { };
  }

  const resizeObserver = new ResizeObserver(() => {
    // Debounce: wait 150ms after last resize before notifying
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    debounceTimer = setTimeout(() => {
      notifyHeightChange();
      debounceTimer = null;
    }, 150) as unknown as number;
  });

  resizeObserver.observe(document.body);

  // Initial notification (immediate)
  notifyHeightChange(true);

  // Return cleanup function
  return () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    if (rafId) {
      cancelAnimationFrame(rafId);
    }
    resizeObserver.disconnect();
  };
};

/**
 * Listen for messages from parent window
 */
export const listenToParentMessages = (
  callback: (message: IframeMessage) => void
): (() => void) => {
  const handler = (event: MessageEvent) => {
    // Add origin validation in production
    if (event.data && typeof event.data === 'object') {
      callback(event.data as IframeMessage);
    }
  };

  window.addEventListener('message', handler);

  // Return cleanup function
  return () => {
    window.removeEventListener('message', handler);
  };
};

/**
 * Notify parent that iframe is ready
 * Uses a flag to prevent multiple notifications during the same session
 */
let iframeReadyNotified = false;

/**
 * Reset the iframe ready notification flag
 * Call this when the modal is reopened to allow a fresh IFRAME_READY notification
 */
export const resetIframeReadyFlag = (): void => {
  iframeReadyNotified = false;
};

export const notifyIframeReady = (): void => {
  // Only notify once per session to prevent spam
  if (iframeReadyNotified) {
    return;
  }

  const message = {
    type: 'IFRAME_READY',
    payload: {
      timestamp: Date.now(),
      url: window.location.href
    }
  };

  // Send immediately
  if (isInIframe() && window.parent) {
    window.parent.postMessage(message, '*');
    iframeReadyNotified = true;
  }
};

/**
 * Notify parent to resize modal for confirmation page
 * Switches modal to confirmation style (question icon, compact sizing)
 * Sends message synchronously without any delay
 */
export const notifyPageChange = (
  page: 'confirmation' | 'success' | 'main' | 'offline' | 'offline-eft' | 'offline-eft-bank' | 'offline-non-eft' | 'offline-bank' | 'offline-voucher' | 'offline-wallet'
): void => {
  if (!isInIframe()) return;

  let message;

  // Send RESIZE_MODAL for confirmation and offline pages with specific dimensions
  if (page === 'confirmation') {
    message = {
      type: 'RESIZE_MODAL',
      payload: {
        width: 390,
        height: 280,
        title: 'Confirmation'
      }
    };
  } else if (page === 'offline-eft') {
    // EFT method offline modal with update banking option
    message = {
      type: 'RESIZE_MODAL',
      payload: {
        width: 390,
        height: 320,
        title: 'Bank Offline'
      }
    };
  } else if (page === 'offline-eft-bank') {
    // Selected EFT bank offline modal with continue and cancel actions
    message = {
      type: 'RESIZE_MODAL',
      payload: {
        width: 390,
        height: 280,
        title: 'Bank Offline'
      }
    };
  } else if (page === 'offline-bank') {
    // Bank-based offline modal (Cash Send, InstantMoney, eWallet, Send-iMali/MobiMoney)
    // No update option, so decreased height
    message = {
      type: 'RESIZE_MODAL',
      payload: {
        width: 390,
        height: 243,
        title: 'Bank Offline'
      }
    };
  } else if (page === 'offline-wallet') {
    // Wallet-based offline modal 
    message = {
      type: 'RESIZE_MODAL',
      payload: {
        width: 390,
        height: 360,
        title: 'Wallet Withdrawal Offline'
      }
    };
  } else if (page === 'offline-non-eft' || page === 'offline-voucher') {
    // Voucher-based offline modal (Vouchers, Cash Vouchers, Wallets)
    message = {
      type: 'RESIZE_MODAL',
      payload: {
        width: 390,
        height: 360,
        title: 'Voucher Withdrawal Offline'
      }
    };
  }

  if (message && window.parent) {
    // Send immediately, no RAF or delay
    window.parent.postMessage(message, '*');
  }
};

/**
 * Notify parent of initial withdrawal app dimensions based on history
 * Uses a flag to prevent multiple notifications during the same session
 * Sends immediately before content renders to prevent visible resizing
 */
let initialDimensionsNotified = false;

interface WithdrawalDimensionOptions {
  isAfricaBet?: boolean;
  paymentMethodCount?: number;
  expandedContentHeight?: number;
}

const STANDARD_INITIAL_WITH_HISTORY_HEIGHT = 710;
const STANDARD_INITIAL_WITHOUT_HISTORY_HEIGHT = 630;
const STANDARD_COLLAPSED_WITHOUT_HISTORY_HEIGHT = 610;
const LARGE_SCREEN_MIN_WIDTH = 1024;
const LARGE_SCREEN_HISTORY_HEIGHT_REDUCTION = 56;
const AFRICABET_REFERENCE_METHOD_COUNT = 8;
const ACCORDION_HEADER_HEIGHT = 40;
const ACCORDION_ROW_GAP = 8;
const ACCORDION_ROW_HEIGHT = ACCORDION_HEADER_HEIGHT + ACCORDION_ROW_GAP;

const isLargeScreen = (): boolean => {
  const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
  const screenWidth = window.screen?.width ?? viewportWidth;

  return Math.max(viewportWidth, screenWidth) >= LARGE_SCREEN_MIN_WIDTH;
};

const getResponsiveWithdrawalHeight = (height: number, hasHistory: boolean): number => {
  if (!hasHistory || !isLargeScreen()) {
    return height;
  }

  return Math.max(height - LARGE_SCREEN_HISTORY_HEIGHT_REDUCTION, 0);
};

const getAfricaBetAccordionHeight = (
  standardHeight: number,
  paymentMethodCount?: number,
  expandedContentHeight: number = 0,
): number => {
  const methodCount = Math.max(paymentMethodCount ?? 1, 1);
  const countDifference = methodCount - AFRICABET_REFERENCE_METHOD_COUNT;

  return Math.round(
    standardHeight
      + (countDifference * ACCORDION_ROW_HEIGHT)
      + Math.max(expandedContentHeight, 0),
  );
};

/**
 * Reset the initial dimensions notification flag
 * Call this when the modal is reopened to allow a fresh resize notification
 */
export const resetInitialDimensionsFlag = (): void => {
  initialDimensionsNotified = false;
};

export const notifyInitialWithdrawalDimensions = (
  hasHistory: boolean,
  options: WithdrawalDimensionOptions = {}
): void => {
  // Only notify once per session
  if (initialDimensionsNotified) {
    return;
  }

  if (!isInIframe()) return;

  const baseStandardHeight = hasHistory
    ? STANDARD_INITIAL_WITH_HISTORY_HEIGHT
    : STANDARD_INITIAL_WITHOUT_HISTORY_HEIGHT;
  const standardHeight = getResponsiveWithdrawalHeight(baseStandardHeight, hasHistory);
  const height = options.isAfricaBet
    ? getAfricaBetAccordionHeight(standardHeight, options.paymentMethodCount)
    : standardHeight;

  const message = {
    type: 'RESIZE_MODAL',
    payload: {
      width: 560,
      height,
      title: 'Withdrawals'
    }
  };

  if (window.parent) {
    // Send immediately, synchronously before any rendering
    window.parent.postMessage(message, '*');
    initialDimensionsNotified = true;
  }
};

/**
 * Notify parent to resize modal when method dropdown is expanded
 * Sends immediately for instant response
 */
export const notifyMethodDropdownExpanded = (
  hasHistory: boolean = false,
  options: WithdrawalDimensionOptions = {},
): void => {
  if (!isInIframe()) return;

  const baseCollapsedHeight = hasHistory
    ? STANDARD_INITIAL_WITH_HISTORY_HEIGHT
    : STANDARD_COLLAPSED_WITHOUT_HISTORY_HEIGHT;
  const collapsedHeight = getResponsiveWithdrawalHeight(baseCollapsedHeight, hasHistory);
  const height = options.isAfricaBet
    ? getAfricaBetAccordionHeight(
        collapsedHeight,
        options.paymentMethodCount,
        options.expandedContentHeight,
      )
    : 964;

  const message = {
    type: 'RESIZE_MODAL',
    payload: {
      width: 560,
      height,
      title: 'Withdrawals'
    }
  };

  if (window.parent) {
    // Send immediately without delay
    window.parent.postMessage(message, '*');
  }
};

/**
 * Notify parent to resize modal when method dropdown is collapsed (no history)
 * Sends immediately for instant response
 */
export const notifyMethodDropdownCollapsed = (
  hasHistory: boolean = false,
  options: WithdrawalDimensionOptions = {}
): void => {
  if (!isInIframe()) return;

  const baseStandardHeight = hasHistory
    ? STANDARD_INITIAL_WITH_HISTORY_HEIGHT
    : STANDARD_COLLAPSED_WITHOUT_HISTORY_HEIGHT;
  const standardHeight = getResponsiveWithdrawalHeight(baseStandardHeight, hasHistory);
  const height = options.isAfricaBet
    ? getAfricaBetAccordionHeight(standardHeight, options.paymentMethodCount)
    : standardHeight;

  const message = {
    type: 'RESIZE_MODAL',
    payload: {
      width: 560,
      height,
      title: 'Withdrawals'
    }
  };

  if (window.parent) {
    // Send immediately without delay
    window.parent.postMessage(message, '*');
  }
};

/**
 * Notify parent of withdrawal completion (success or failure)
 * Premium will close modal and show toast notification
 */
export const notifyWithdrawalComplete = (success: boolean, message: string): void => {
  if (!isInIframe()) return;

  const notification = {
    type: 'WITHDRAWAL_COMPLETE',
    payload: {
      success,
      message,
      timestamp: Date.now()
    }
  };

  if (window.parent) {
    window.parent.postMessage(notification, '*');
  }
};

/**
 * Notify parent that an offline withdrawal is being initiated
 */
export const notifyOfflineWithdrawalStarted = (methodName: string, amount: string): void => {
  if (!isInIframe()) return;

  const notification = {
    type: 'OFFLINE_WITHDRAWAL_STARTED',
    payload: {
      methodName,
      amount,
      timestamp: Date.now()
    }
  };

  if (window.parent) {
    window.parent.postMessage(notification, '*');
  }
};

/**
 * Notify parent that user wants to update banking details
 */
export const notifyUpdateBankingDetails = (premiumSourceUrl: string|null): void => {
  if (!isInIframe()) return;
 
  if (window.parent) {
    if(premiumSourceUrl)
                window.open(`${premiumSourceUrl}/account/my-profile/my-banking-details`, '_blank', 'noopener,noreferrer');
  }
};

/**
 * Notify parent of offline modal step changes for proper resizing
 * Sends immediately for instant visual update
 */
export const notifyOfflineModalStep = (step: 'initial' | 'confirmation', methodName: string): void => {
  if (!isInIframe()) return;

  // Send resize message with confirmation dimensions
  const resizeMessage = {
    type: 'RESIZE_MODAL',
    payload: {
      width: 390,
      height: 280,
      title: 'Confirmation'
    }
  };

  const notification = {
    type: 'OFFLINE_MODAL_STEP',
    payload: {
      step,
      methodName,
      timestamp: Date.now()
    }
  };

  if (window.parent) {
    // Send both messages immediately
    window.parent.postMessage(resizeMessage, '*');
    window.parent.postMessage(notification, '*');
  }
};

/**
 * Notify parent to resize modal for voucher offline screen
 * Sends immediately for instant visual update
 */
export const notifyVoucherOffline = (): void => {
  if (!isInIframe()) return;

  const message = {
    type: 'RESIZE_MODAL',
    payload: {
      width: 390,
      height: 440,
      title: 'Voucher Withdrawal Offline'
    }
  };

  if (window.parent) {
    // Send immediately without delay
    window.parent.postMessage(message, '*');
  }
};

/**
 * Notify parent to close the modal
 */
export const notifyCloseModal = (): void => {
  if (!isInIframe()) return;

  const message = {
    type: 'CLOSE_MODAL',
    payload: {
      timestamp: Date.now()
    }
  };

  if (window.parent) {
    window.parent.postMessage(message, '*');
  }
};

export const notifyBankVerification = (): void => {
  if (!isInIframe()) return;

  const message = {
    type: 'RESIZE_MODAL',
    payload: {
      width: 390,
      height: 200,
      title: 'Verifying Bank'
    }
  };

  if (window.parent) {
    window.parent.postMessage(message, '*');
  }
};

/**
 * Send debug logs to parent window for debugging
 */
export const notifyDebugLog = (label: string, data: any): void => {
  console.log(label, data);
  
  if (!isInIframe()) return;

  const message = {
    type: 'DEBUG_LOG',
    payload: {
      label,
      data,
      timestamp: Date.now()
    }
  };

  if (window.parent) {
    window.parent.postMessage(message, '*');
  }
};
